import React, { useContext, useState, useEffect, useCallback, useMemo } from "react";
import { auth } from "../firebase";
import firebase from "firebase/compat/app";
import { internalGetReq } from "../api/requestFunctions";
import { API_AUTH_TYPES, API_ROUTES } from "../api/apiConstants";
import { AUTH_SESSION_STORAGE_KEY } from "../helpers/authStorage";
import queryClient from "../queryClient";

const AuthContext = React.createContext();

/** Returns the AuthContext value. Must be used inside AuthProvider. */
export function useAuth() {
  return useContext(AuthContext);
}

/**
 * Decode a JWT token payload
 * @param {string} token - JWT token string
 * @returns {object|null} Decoded payload or null if invalid
 */
function decodeToken(token) {
  if (!token) return null;
  try {
    const payload = token.split(".")[1];
    return JSON.parse(atob(payload));
  } catch {
    return null;
  }
}

function getTokenCacheScope(token) {
  const payload = decodeToken(token);
  if (!payload) return null;
  return JSON.stringify({
    userId: payload.user_id ?? null,
    firmId: payload.firm_id ?? null,
    householdId: payload.household_id ?? null,
    impersonation: payload.impersonation ?? null,
    isAdmin: Boolean(payload.is_admin),
    isAdvisor: Boolean(payload.is_advisor),
    isManager: Boolean(payload.is_manager),
    isSuperAdmin: Boolean(payload.is_super_admin),
    isBlogEditor: Boolean(payload.is_blog_editor),
    isTravelLoungeEditor: Boolean(payload.is_travel_lounge_editor),
    isLearningLabEditor: Boolean(payload.is_learning_lab_editor),
  });
}

/**
 * Provides authentication state and methods to the component tree.
 * Manages Firebase auth, JWT tokens (synced to localStorage), and decoded token data.
 *
 * Context value includes:
 *   isAdmin, isAdvisor — role flags fetched from the API when the access token changes
 *   currentUser — Firebase auth user object
 *   accessToken, refreshToken, calendlyLink — JWT tokens (synced to localStorage)
 *   setAccessToken, setRefreshToken, setCalendlyLink — setters that also sync to localStorage
 *   decodedToken — decoded JWT payload
 *   isSuperAdmin, isImpersonating, impersonationData — derived from JWT claims
 *   authLogin, loginLocal, loginSession, logout, signup, resetPassword, updateEmail, updatePassword — auth methods
 *   reAuthNextURL, setreAuthNextURL — for redirecting after re-authentication
 */
export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState();
  const [loading, setLoading] = useState(true);
  const [reAuthNextURL, setreAuthNextURL] = useState("");
  const [isAdmin, setIsAdmin] = useState(false);
  const [isAdvisor, setIsAdvisor] = useState(false);

  // Token state - initialized from localStorage
  const [accessToken, setAccessTokenState] = useState(() => localStorage.getItem(AUTH_SESSION_STORAGE_KEY.ACCESS_TOKEN));
  const [refreshToken, setRefreshTokenState] = useState(() => localStorage.getItem(AUTH_SESSION_STORAGE_KEY.REFRESH_TOKEN));
  const [calendlyLink, setCalendlyLinkState] = useState(() => localStorage.getItem(AUTH_SESSION_STORAGE_KEY.CALENDLY_LINK));

  // Setters that sync to localStorage
  const setAccessToken = useCallback((token) => {
    const previousToken = localStorage.getItem(AUTH_SESSION_STORAGE_KEY.ACCESS_TOKEN);
    const previousScope = getTokenCacheScope(previousToken);
    const nextScope = getTokenCacheScope(token);
    const isNewAuthenticatedScope = !!token && (
      !previousToken
      || !previousScope
      || !nextScope
      || previousScope !== nextScope
    );

    // Query keys are not consistently actor-scoped. Clear synchronously whenever
    // authentication is removed or its identity, tenant, impersonation, or authorization
    // claims change, so a fresh token cannot inherit stale cached data.
    if (!token || isNewAuthenticatedScope) {
      queryClient.clear();
      setIsAdvisor(false);
      setIsAdmin(false);
    }

    if (token) {
      localStorage.setItem(AUTH_SESSION_STORAGE_KEY.ACCESS_TOKEN, token);
    } else {
      localStorage.removeItem(AUTH_SESSION_STORAGE_KEY.ACCESS_TOKEN);
    }
    setAccessTokenState(token);
  }, []);

  const setRefreshToken = useCallback((token) => {
    if (token) {
      localStorage.setItem(AUTH_SESSION_STORAGE_KEY.REFRESH_TOKEN, token);
    } else {
      localStorage.removeItem(AUTH_SESSION_STORAGE_KEY.REFRESH_TOKEN);
    }
    setRefreshTokenState(token);
  }, []);

  const setCalendlyLink = useCallback((link) => {
    if (link) {
      localStorage.setItem(AUTH_SESSION_STORAGE_KEY.CALENDLY_LINK, link);
    } else {
      localStorage.removeItem(AUTH_SESSION_STORAGE_KEY.CALENDLY_LINK);
    }
    setCalendlyLinkState(link);
  }, []);

  // Derived values from JWT token
  const decodedToken = useMemo(() => decodeToken(accessToken), [accessToken]);
  const isSuperAdmin = decodedToken?.is_super_admin || false;
  const isImpersonating = decodedToken?.impersonation?.active || false;
  const impersonationData = decodedToken?.impersonation || null;

  /** Creates a new Firebase account with email/password. */
  function signup(email, password) {
    return auth.createUserWithEmailAndPassword(email, password);
  }

  /** Signs in with email/password using LOCAL persistence across browser tabs. */
  const authLogin = async (email, password) => {
    // Always use LOCAL persistence so auth works across browser tabs
    await auth.setPersistence(firebase.auth.Auth.Persistence.LOCAL);
    return auth.signInWithEmailAndPassword(email, password);
  };

  /** Signs in with LOCAL persistence (persists across browser restarts). */
  const loginLocal = (email, password) => {
    auth
      .setPersistence(firebase.auth.Auth.Persistence.LOCAL)
      .then(() => {
        return auth.signInWithEmailAndPassword(email, password);
      })
      .catch((error) => {
      });
  };

  /** Signs in with SESSION persistence (clears on browser close). */
  const loginSession = (email, password) => {
    auth
      .setPersistence(firebase.auth.Auth.Persistence.SESSION)
      .then(() => {
        return auth.signInWithEmailAndPassword(email, password);
      })
      .catch((error) => {
      });
  };

  /** Signs out the current user and clears all tokens from state and localStorage. */
  function logout() {
    setAccessToken(null);
    setRefreshToken(null);
    setCalendlyLink(null);
    // Reset role flags so advisor-gated effects (parabot queries, activity
    // tracking) stop firing immediately on the /login screen after logout.
    setIsAdvisor(false);
    setIsAdmin(false);
    return auth.signOut();
  }

  /** Sends a password reset email. */
  function resetPassword(email) {
    return auth.sendPasswordResetEmail(email);
  }

  /** Updates the current user's email address. */
  function updateEmail(email) {
    return currentUser.updateEmail(email);
  }

  /** Updates the current user's password. */
  function updatePassword(password) {
    return currentUser.updatePassword(password);
  }

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      setCurrentUser(user);
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  useEffect(() => {
    let cancelled = false;
    const getAuth = async () => {
      try {
        // Only fetch if we have an access token
        if (!accessToken) return;
        // Skip the request if the JWT is already expired — firing it would
        // just produce a known 401 from the auth middleware. Stale tokens in
        // localStorage were the dominant source of get_user 4xx noise.
        const exp = decodeToken(accessToken)?.exp;
        if (typeof exp === "number" && exp * 1000 <= Date.now()) {
          setAccessToken(null);
          return;
        }
        const response = await internalGetReq(
          API_AUTH_TYPES.USER,
          API_ROUTES.USERS,
          "get_user"
        );
        if (!cancelled) {
          setIsAdmin(response.is_admin);
          setIsAdvisor(response.is_advisor);
        }
      } catch (error) {
        // Silently fail if user data can't be fetched
      }
    };
    getAuth();

    return () => { cancelled = true; };
  }, [accessToken, setAccessToken]);

  const value = {
    // Auth state
    isAdmin,
    isAdvisor,
    setIsAdvisor,
    setIsAdmin,
    currentUser,
    // Token state
    accessToken,
    refreshToken,
    calendlyLink,
    setAccessToken,
    setRefreshToken,
    setCalendlyLink,
    // Decoded token data
    decodedToken,
    isSuperAdmin,
    isImpersonating,
    impersonationData,
    // Auth methods
    loginLocal,
    loginSession,
    authLogin,
    signup,
    logout,
    resetPassword,
    updateEmail,
    updatePassword,
    reAuthNextURL,
    setreAuthNextURL,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}
